import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import { Box, Chip, CircularProgress, Grid, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Panel } from 'src/UIKit';
import { TintedPanel } from './TintedPanel.styles';
import { useCampaignsApi } from 'src/modules/campaigns/api';
import { TextToWinDrawStatus, TextToWinWinner } from 'src/modules/campaigns/types';

interface TextToWinWinnersProps {
  campaignId: string;
  companyId: string;
}

const drawStatusChip: Record<
  TextToWinDrawStatus,
  { label: string; color: 'default' | 'success' | 'warning' | 'error' }
> = {
  pending: { label: 'Draw Pending', color: 'warning' },
  processing: { label: 'Draw In Progress', color: 'warning' },
  completed: { label: 'Draw Completed', color: 'success' },
  failed: { label: 'Draw Failed', color: 'error' },
};

const getDisplayName = (winner: TextToWinWinner): string => {
  const { firstName, lastName, phoneNumber } = winner.contact;
  const name = [firstName, lastName].filter(Boolean).join(' ');
  return name || phoneNumber;
};

const getEmptyStateText = (drawStatus: TextToWinDrawStatus | null): string => {
  switch (drawStatus) {
    case 'failed':
      return 'The draw failed. Winners are not available yet.';
    case 'processing':
      return 'The draw is in progress. Winners will appear here when it is completed.';
    default:
      return 'Winners will appear here once the draw is completed.';
  }
};

export const TextToWinWinners: React.FC<TextToWinWinnersProps> = ({
  campaignId,
  companyId,
}) => {
  const { getTextToWinWinners } = useCampaignsApi();
  const [loading, setLoading] = useState(true);
  const [drawStatus, setDrawStatus] = useState<TextToWinDrawStatus | null>(null);
  const [winners, setWinners] = useState<TextToWinWinner[]>([]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getTextToWinWinners(companyId, campaignId)
      .then(({ data }) => {
        if (!active) {
          return;
        }
        setDrawStatus(data.drawStatus);
        setWinners(data.winners || []);
      })
      .catch(() => {
        if (active) {
          setDrawStatus(null);
          setWinners([]);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [campaignId, companyId, getTextToWinWinners]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mb={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!drawStatus && winners.length === 0) {
    return null;
  }

  const chip = drawStatus ? drawStatusChip[drawStatus] : null;

  return (
    <TintedPanel tone="warning" sx={{ mb: 2.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <EmojiEventsIcon color="warning" />
          <Box>
            <Typography variant="subtitle1" fontWeight="fontWeightSemiBold">
              Winners
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Text to Win campaign results
            </Typography>
          </Box>
        </Stack>
        {chip && <Chip label={chip.label} color={chip.color} size="small" />}
      </Stack>

      {winners.length > 0 ? (
        <Grid container spacing={2}>
          {winners.map((winner, index) => (
            <Grid item xs={12} md={6} key={winner.id}>
              <Panel sx={{ p: 1.5, display: 'flex', gap: 1.5 }}>
                <Stack spacing={0.5}>
                  <Typography variant="body2" fontWeight="fontWeightSemiBold">
                    #{index + 1} {getDisplayName(winner)}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.75} mt={0.5} fontSize={16}>
                    <PhoneIphoneOutlinedIcon fontSize="inherit" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {winner.contact.phoneNumber}
                    </Typography>
                  </Stack>
                  {winner.contact.email && (
                    <Stack direction="row" alignItems="center" spacing={0.75} mt={0.25} fontSize={16}>
                      <EmailOutlinedIcon fontSize="inherit" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {winner.contact.email}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Panel>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {getEmptyStateText(drawStatus)}
        </Typography>
      )}
    </TintedPanel>
  );
};
